import { IssueSeverity, MODULE } from '@gearbelt/global';
import { useQuery } from '@tanstack/react-query';
import Chart from 'react-apexcharts';

import WarningIcon from '@/components/icons/warning';
import { getDashboardIssuesSummaryQueryOptions } from '@/queries/dashboard';
import { useModulesStore } from '@/stores/modules.store';
import { useWorkspaceNavigate } from '@/utils/routes';

export const IssueSummary = () => {
  const { data: response } = useQuery(getDashboardIssuesSummaryQueryOptions());
  const data = response?.data;
  const navigate = useWorkspaceNavigate();
  const { setModuleFilters } = useModulesStore();

  const handleChartClick = (_event: unknown, _chartContext: unknown, config: { dataPointIndex: number }) => {
    const clickedIndex = config.dataPointIndex;

    const severityMapping = [IssueSeverity.Low, IssueSeverity.Medium, IssueSeverity.High, IssueSeverity.Critical];
    const selectedSeverity = severityMapping[clickedIndex];

    if (selectedSeverity) {
      setModuleFilters(MODULE.Issues, [
        {
          id: 'severity',
          value: [selectedSeverity],
        },
      ]);

      navigate('/issues');
    }
  };

  const options = {
    chart: {
      toolbar: {
        show: false,
      },
      events: {
        dataPointSelection: handleChartClick,
      },
    },
    grid: {
      padding: {
        top: 15,
        bottom: 5,
      },
    },
    labels: ['Low', 'Medium', 'High', 'Critical'],
    dataLabels: {
      enabled: false,
    },
    legend: { show: false },
    stroke: { width: 0 },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
        },
      },
    },
    colors: ['rgb(224, 226, 228)', 'rgb(246, 187, 45)', 'rgb(193, 50, 50)', 'rgb(227, 81, 77)'],
    tooltip: {
      custom: function ({ series, seriesIndex, w }: { series: number[]; seriesIndex: number; w: { globals: { labels: string[] } } }) {
        const value = series[seriesIndex];
        const label = w.globals.labels[seriesIndex];

        return `<div class="px-1 py-[2px] rounded text-xs text-white bg-default-text">${label}: ${value}</div>`;
      },
    },
  };

  const total = data ? Object.values(data).reduce((acc, cur) => acc + cur, 0) : 0;

  return data !== null ? (
    <div className="flex flex-col gap-2">
      <span className="text-xl leading-[30px] font-medium">Issues Summary</span>

      <div className="card-body relative h-[180px] p-0 [&_.apexcharts-pie-area]:cursor-pointer">
        <Chart options={options} series={data ? [data.low, data.medium, data.high, data.critical] : []} type="donut" height={173} />

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          {total > 0 && <span className="pt-1 text-xl/[30px] font-bold">{total}</span>}
        </div>
      </div>

      <div className="flex text-center">
        <div className="flex h-[101px] w-1/4 flex-col gap-1">
          <div className="flex justify-center pt-[10px]">
            <div className="grid size-5 place-items-center">
              <div className="border-grey-5 bg-grey-1 size-[15px] rounded-full border-[3px]"></div>
            </div>
          </div>
          <div className="flex h-[30px] items-center justify-center text-xl/[30px] font-bold">{data?.low || '--'}</div>
          <div className="text-secondary-text mb-0 h-[21px] text-sm/[21px] font-normal">Low</div>
        </div>
        <div className="flex h-[101px] w-1/4 flex-col gap-1">
          <div className="flex justify-center pt-[10px]">
            <div className="grid size-5 place-items-center">
              <div className="border-default-yellow bg-default-light-yellow size-[15px] rounded-full border-[3px]"></div>
            </div>
          </div>
          <div className="flex h-[30px] items-center justify-center text-xl/[30px] font-bold">{data?.medium || '--'}</div>
          <div className="text-secondary-text mb-0 h-[21px] text-sm/[21px] font-normal">Medium</div>
        </div>
        <div className="flex h-[101px] w-1/4 flex-col gap-1">
          <div className="flex justify-center pt-[10px]">
            <div className="grid size-5 place-items-center">
              <div className="border-default-dark-red bg-secondary-light-red size-[15px] rounded-full border-[3px]"></div>
            </div>
          </div>
          <div className="flex h-[30px] items-center justify-center text-xl/[30px] font-bold">{data?.high || '--'}</div>
          <div className="text-secondary-text mb-0 h-[21px] text-sm/[21px] font-normal">High</div>
        </div>
        <div className="flex h-[101px] w-1/4 flex-col gap-1">
          <div className="flex justify-center pt-[10px]">
            <WarningIcon className="text-default-red size-4" />
          </div>
          <div className="flex h-[30px] items-center justify-center text-xl/[30px] font-bold">{data?.critical || '--'}</div>
          <div className="text-secondary-text mb-0 h-[21px] text-sm/[21px] font-normal">Critical</div>
        </div>
      </div>
    </div>
  ) : null;
};
